'use client';

/**
 * Accent-lit promo rail — a horizontally scrolling row of wide topic
 * cards, each with its own accent colour, artwork bleeding off the
 * right edge and a CTA pill. The BTK "category strip" treatment.
 *
 * <pre>
 *   ‹  ┌────────────────┬────────────────┬────────────────┐  ›
 *      │ Title          │ Title          │ Title          │
 *      │ Two-line copy  │ Two-line copy  │ Two-line copy  │
 *      │ [ CTA → ] ▓img │ [ CTA → ] ▓img │ [ CTA → ] ▓img │
 *      └────────────────┴────────────────┴────────────────┘
 * </pre>
 *
 * <p>Every visual decision an author is likely to want is a prop — card
 * count per view, height, radius, gap, artwork width, card composition,
 * CTA treatment, tone, arrows and scroll behaviour — so the block is
 * fully drivable from the admin without a code change. Per-card accent
 * colour drives the gradient, the border and the hover glow.
 *
 * <h3>Card variants</h3>
 * <ul>
 *   <li>{@code split} (default) — copy on the left, artwork bleeding off
 *       the right edge behind a horizontal scrim. Matches the reference.</li>
 *   <li>{@code overlay} — artwork covers the whole card, copy sits over a
 *       bottom-up scrim.</li>
 *   <li>{@code plain} — no artwork; the accent gradient carries the card.</li>
 * </ul>
 */
import { useId, useMemo, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PortalLink,
  isVisible,
  useCurrentUser,
  type CurrentUser,
} from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { HeroEmptyFallback } from '../_shared/HeroEmptyFallback';
import { tx } from '../_shared/coerce';
import type { PaddingBox } from '../_shared/resolvePadding';
import { resizeImageUrl } from '../_shared/resizeImageUrl';
import { SectionShell } from '../_shared/SectionShell';
import {
  ContentRail,
  type ContentRailArrows,
  type ContentRailScrollMode,
  type ContentRailSnap,
} from '../ui/ContentRail';
import type { CampaignAudience } from '../_shared/campaignAudience';

type Localized = string | Record<string, string>;

export interface HeroPromoRailCard extends CampaignAudience {
  title?: Localized;
  description?: Localized;
  ctaLabel?: Localized;
  href?: string;
  /** Artwork bleeding off the card's right edge (or covering it, in
   *  {@code overlay}). Omit for a copy-only card. */
  image?: string;
  imageAlt?: string;
  /** Small square mark drawn before the title (image URL). */
  icon?: string;
  /** Any CSS colour. Drives the gradient, border and hover glow.
   *  Falls back to the block's {@code accent}. */
  accent?: string;
  /** Optional chip above the title. */
  badge?: Localized;
}

export type HeroPromoRailVariant = 'split' | 'overlay' | 'plain';
export type HeroPromoRailCta = 'ghost' | 'solid' | 'link';
export type HeroPromoRailTone = 'dark' | 'light';

export interface HeroPromoRailProps {
  // — Section chrome ————————————————————————————————————————————————
  title?: Localized;
  subtitle?: Localized;
  kicker?: Localized;
  headingLine?: boolean;
  /** Section background. Any CSS colour/gradient; transparent by default. */
  background?: string;

  // — Content ——————————————————————————————————————————————————————
  cards?: HeroPromoRailCard[];

  // — Card looks ————————————————————————————————————————————————————
  variant?: HeroPromoRailVariant;
  tone?: HeroPromoRailTone;
  /** Fallback accent for cards that don't set their own. */
  accent?: string;
  /** How much of the accent bleeds into the card background, 0-100.
   *  Defaults to the tone's own weight (55 dark / 18 light). */
  accentStrength?: number;
  ctaStyle?: HeroPromoRailCta;
  /** Cards visible at once on desktop (1–5). Steps down on narrow viewports. */
  cardsPerView?: number;
  cardHeight?: number;
  cardRadius?: number;
  gap?: number;
  /** Artwork width as a % of the card, {@code split} only. */
  imageWidth?: number;

  // — Rail behaviour ————————————————————————————————————————————————
  arrows?: ContentRailArrows;
  snap?: ContentRailSnap;
  scrollMode?: ContentRailScrollMode;
  peek?: boolean;
  edgeFade?: boolean;

  // — Layout ————————————————————————————————————————————————————————
  /** 'full' (default) lets the rail span the section edge-to-edge;
   *  'theme' constrains it to the page's max-width container. */
  maxWidth?: 'full' | 'theme';
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
  style?: CSSProperties;
}

interface ToneSpec {
  base: string;
  title: string;
  body: string;
  ctaBorder: string;
  ctaText: string;
  ctaSolidText: string;
  badgeBg: string;
  /** Default accent weight in the card gradient when the author hasn't set
   *  {@code accentStrength} — dark cards take far more colour before the
   *  copy stops reading. */
  wash: number;
  borderMix: number;
}

const TONES: Record<HeroPromoRailTone, ToneSpec> = {
  dark: {
    base: '#0b0f19',
    title: '#ffffff',
    body: 'rgba(255,255,255,0.64)',
    ctaBorder: 'rgba(255,255,255,0.28)',
    ctaText: '#ffffff',
    ctaSolidText: '#0b0f19',
    badgeBg: 'rgba(255,255,255,0.12)',
    wash: 55,
    borderMix: 62,
  },
  light: {
    base: '#ffffff',
    title: '#12141a',
    body: 'rgba(18,20,26,0.62)',
    ctaBorder: 'rgba(18,20,26,0.18)',
    ctaText: '#12141a',
    ctaSolidText: '#ffffff',
    badgeBg: 'rgba(18,20,26,0.06)',
    wash: 18,
    borderMix: 30,
  },
};

const DEFAULT_ACCENT = 'var(--color-brand-500, #3b5bdb)';

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

export function HeroPromoRail({
  title,
  subtitle,
  kicker,
  headingLine = false,
  background,
  cards = [],
  variant = 'split',
  tone = 'dark',
  accent = DEFAULT_ACCENT,
  accentStrength,
  ctaStyle = 'ghost',
  cardsPerView = 4,
  cardHeight = 200,
  cardRadius = 14,
  gap = 16,
  imageWidth = 52,
  arrows = 'always',
  snap = 'start',
  scrollMode,
  peek = false,
  edgeFade = false,
  maxWidth = 'full',
  paddingOuter,
  paddingInner,
  style,
}: HeroPromoRailProps) {
  const rawId = useId();
  const scope = `hpr${rawId.replace(/[^a-zA-Z0-9]/g, '')}`;
  // Aliased: `t` is already the resolved tone spec in this component.
  const { t: translate } = useTranslation('site-builder-blocks');
  const currentUser = useCurrentUser<CurrentUser>();
  const visibleCards = useMemo(
    () => cards.filter((c) => isVisible(c, currentUser)),
    [cards, currentUser],
  );

  const t = TONES[tone] ?? TONES.dark;
  const perView = clamp(Math.round(cardsPerView) || 4, 1, 5);
  const g = clamp(gap, 0, 64);
  const h = clamp(cardHeight, 120, 640);
  const radius = clamp(cardRadius, 0, 40);
  const imgW = clamp(imageWidth, 20, 80);

  // The accent has to read as THE card's colour, not a hint of one: it
  // opens the gradient at full strength, still tints the far corner, and
  // tints the artwork scrim too — otherwise the half of the card behind
  // the artwork falls back to neutral base and the card reads grey.
  const strength = clamp(Math.round(accentStrength ?? t.wash), 0, 100);
  const midStrength = Math.round(strength * 0.45);
  const tailStrength = Math.round(strength * 0.18);
  const scrimTint = `color-mix(in srgb, var(--hpr-accent) ${Math.round(strength * 0.6)}%, ${t.base})`;

  // Only ever step DOWN from the author's choice — a rail set to 2-up
  // must not widen to 3-up on a tablet.
  const perViewMd = Math.min(perView, 3);
  const perViewSm = Math.min(perView, 2);
  const itemWidth = (n: number) => `calc((100% - ${g * (n - 1)}px) / ${n})`;

  const css = `
    .${scope}-rail { --hpr-item-w: ${itemWidth(perView)}; }
    @media (max-width: 1280px) { .${scope}-rail { --hpr-item-w: ${itemWidth(perViewMd)}; } }
    @media (max-width: 860px)  { .${scope}-rail { --hpr-item-w: ${itemWidth(perViewSm)}; } }
    @media (max-width: 620px)  { .${scope}-rail { --hpr-item-w: 84%; } }

    .${scope}-card {
      position: relative;
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: ${h}px;
      border-radius: ${radius}px;
      overflow: hidden;
      text-decoration: none;
      border: 1px solid color-mix(in srgb, var(--hpr-accent) ${t.borderMix}%, transparent);
      background:
        linear-gradient(
          135deg,
          color-mix(in srgb, var(--hpr-accent) ${strength}%, ${t.base}) 0%,
          color-mix(in srgb, var(--hpr-accent) ${midStrength}%, ${t.base}) 58%,
          color-mix(in srgb, var(--hpr-accent) ${tailStrength}%, ${t.base}) 100%
        );
      transition: transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease;
    }
    .${scope}-card:hover {
      transform: translateY(-3px);
      border-color: color-mix(in srgb, var(--hpr-accent) 90%, transparent);
      box-shadow: 0 18px 44px color-mix(in srgb, var(--hpr-accent) 30%, transparent);
    }
    .${scope}-card:focus-visible {
      outline: 2px solid color-mix(in srgb, var(--hpr-accent) 90%, transparent);
      outline-offset: 3px;
    }
    .${scope}-art {
      position: absolute;
      inset: 0 0 0 auto;
      width: ${variant === 'overlay' ? '100%' : `${imgW}%`};
      height: 100%;
      object-fit: cover;
      transition: transform 400ms ease;
    }
    .${scope}-card:hover .${scope}-art { transform: scale(1.04); }
    .${scope}-scrim {
      position: absolute;
      inset: 0;
      pointer-events: none;
      background: ${
        variant === 'overlay'
          ? `linear-gradient(0deg, ${scrimTint} 4%, color-mix(in srgb, ${scrimTint} 62%, transparent) 46%, color-mix(in srgb, ${scrimTint} 18%, transparent) 100%)`
          : `linear-gradient(90deg, ${scrimTint} 8%, color-mix(in srgb, ${scrimTint} 62%, transparent) 48%, color-mix(in srgb, ${scrimTint} 12%, transparent) 88%)`
      };
    }
    .${scope}-body {
      position: relative;
      flex: 1 1 auto;
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: clamp(18px, 2.2vw, 26px);
      max-width: ${variant === 'split' ? `${100 - imgW + 14}%` : '100%'};
    }
    @media (max-width: 620px) {
      .${scope}-body { max-width: 100%; }
    }
    .${scope}-cta {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      align-self: flex-start;
      /* auto keeps the CTA pinned to the card's bottom edge no matter how
         short the copy is, so a row of cards has one CTA baseline. */
      margin-top: auto;
      font-size: var(--font-size-sm, 14px);
      font-weight: 600;
      line-height: 1;
      transition: gap 180ms ease, background 180ms ease, border-color 180ms ease;
    }
    .${scope}-card:hover .${scope}-cta { gap: 12px; }
    .${scope}-cta--ghost {
      padding: 10px 16px;
      border-radius: 8px;
      border: 1px solid ${t.ctaBorder};
      color: ${t.ctaText};
      background: transparent;
    }
    .${scope}-card:hover .${scope}-cta--ghost {
      border-color: color-mix(in srgb, var(--hpr-accent) 85%, transparent);
      background: color-mix(in srgb, var(--hpr-accent) 16%, transparent);
    }
    .${scope}-cta--solid {
      padding: 10px 18px;
      border-radius: 8px;
      border: 1px solid transparent;
      background: var(--hpr-accent);
      color: ${t.ctaSolidText};
    }
    .${scope}-cta--link { color: ${t.ctaText}; }
    @media (prefers-reduced-motion: reduce) {
      .${scope}-card, .${scope}-art, .${scope}-cta { transition: none; }
      .${scope}-card:hover { transform: none; }
      .${scope}-card:hover .${scope}-art { transform: none; }
    }
  `;

  return (
    <SectionShell
      title={tx(title) || undefined}
      subtitle={tx(subtitle) || undefined}
      kicker={tx(kicker) || undefined}
      headingLine={headingLine}
      background={background}
      maxWidth={maxWidth}
      paddingOuter={paddingOuter}
      paddingInner={paddingInner}
      style={style}
    >
      <style dangerouslySetInnerHTML={{ __html: css }} />
      {visibleCards.length === 0 ? (
        <HeroEmptyFallback
          style={{
            width: 'min(1100px, 90%)',
            margin: '0 auto',
            minHeight: 'clamp(220px, 40vw, 320px)',
            borderRadius: radius,
          }}
        />
      ) : (
        <ContentRail
          className={`${scope}-rail`}
          ariaLabel={tx(title) || translate('hero.promoRail.label')}
          itemWidth="var(--hpr-item-w, 340px)"
          gap={`${g}px`}
          snap={snap}
          scrollMode={scrollMode}
          arrows={arrows}
          peek={peek}
          edgeFade={edgeFade}
        >
          {visibleCards.map((card, i) => {
            const cardAccent = card.accent?.trim() || accent;
            const cardTitle = tx(card.title);
            const desc = tx(card.description);
            const ctaLabel = tx(card.ctaLabel);
            const badge = tx(card.badge);
            const showArt = variant !== 'plain' && !!card.image;

            return (
              <Card asChild key={i} className="ring-0">
                <PortalLink
                  href={card.href ?? '#'}
                  className={`${scope}-card`}
                  style={{ ['--hpr-accent' as string]: cardAccent } as CSSProperties}
                >
                  {showArt && (
                    <>
                      <img
                        className={`${scope}-art`}
                        src={resizeImageUrl(card.image, '1400x0')}
                        alt={card.imageAlt ?? ''}
                        loading={i < 2 ? undefined : 'lazy'}
                        decoding="async"
                        aria-hidden={card.imageAlt ? undefined : true}
                      />
                      <div className={`${scope}-scrim`} />
                    </>
                  )}

                  <div className={`${scope}-body`}>
                    {badge && (
                      <span
                        style={{
                          alignSelf: 'flex-start',
                          padding: '3px 10px',
                          borderRadius: 999,
                          background: t.badgeBg,
                          color: t.title,
                          fontSize: 'var(--font-size-xs, 12px)',
                          fontWeight: 600,
                        }}
                      >
                        {badge}
                      </span>
                    )}

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {card.icon && (
                        <img
                          src={card.icon}
                          alt=""
                          aria-hidden
                          width={22}
                          height={22}
                          style={{ borderRadius: 6, flexShrink: 0, objectFit: 'cover' }}
                        />
                      )}
                      <span
                        style={{
                          fontFamily: 'var(--font-family-display, inherit)',
                          fontSize: 'clamp(19px, 1.6vw, 24px)',
                          fontWeight: 700,
                          lineHeight: 1.15,
                          letterSpacing: 'var(--font-tracking-snug, -0.015em)',
                          color: t.title,
                        }}
                      >
                        {cardTitle || translate('hero.promoRail.cardTitle')}
                      </span>
                    </div>

                    {desc && (
                      <p
                        style={{
                          margin: 0,
                          fontSize: 'var(--font-size-sm, 14px)',
                          lineHeight: 1.45,
                          color: t.body,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {desc}
                      </p>
                    )}

                    {ctaLabel && (
                      <span className={`${scope}-cta ${scope}-cta--${ctaStyle}`}>
                        {ctaLabel}
                        <span aria-hidden>→</span>
                      </span>
                    )}
                  </div>
                </PortalLink>
              </Card>
            );
          })}
        </ContentRail>
      )}
    </SectionShell>
  );
}
